import {ActionReducerMapBuilder} from '@reduxjs/toolkit';
import {TimeslotsState} from '../stateTypes';
import {actions} from '../actions';
import {getPendingKeysOwnedByRequest} from '../utils';
import {datesForRange, dateKeyForDateTimeString} from '../dateTimeHelpers';
import {groupBy} from '@lib/utils';
import {registerMutation, reservationCompare, resolveMutation} from './mutationUtils';
import {WorkspaceReservation} from '../apiTypes';

export const extendWithWorkspaceReservationsReducers = (builder: ActionReducerMapBuilder<TimeslotsState>) => {
  // Workspace reservations
  builder
    .addCase(actions.fetchWorkspaceReservations.pending, (state, action) => {
      for (const dateKey of datesForRange(action.meta.arg)) {
        state.workspaceReservationsByDate[dateKey] = {
          status: 'pending',
          data: state.workspaceReservationsByDate[dateKey]?.data ?? null,
          requestId: action.meta.requestId,
        };
      }
    })
    .addCase(actions.fetchWorkspaceReservations.fulfilled, (state, action) => {
      const nonCancelledReservations = action.payload.filter((r) => r.status !== 'Cancelled');
      const reservationsByDate = groupBy(nonCancelledReservations, (r) => dateKeyForDateTimeString(r.localStart));
      const pendingForThisRequest = getPendingKeysOwnedByRequest(
        datesForRange(action.meta.arg),
        state.workspaceReservationsByDate,
        action.meta.requestId,
      );
      for (const dateKey of pendingForThisRequest) {
        state.workspaceReservationsByDate[dateKey] = {
          status: 'fulfilled',
          data: reservationsByDate[dateKey] ?? [],
        };
      }
    })
    .addCase(actions.fetchWorkspaceReservations.rejected, (state, action) => {
      const pendingForThisRequest = getPendingKeysOwnedByRequest(
        datesForRange(action.meta.arg),
        state.workspaceReservationsByDate,
        action.meta.requestId,
      );
      for (const dateKey of pendingForThisRequest) {
        state.workspaceReservationsByDate[dateKey] = {
          status: 'rejected',
          data: null,
          errorMessage:
            action.error.message ??
            `Something went wrong loading the workdays between ${action.meta.arg.to} and ${action.meta.arg.from}`,
        };
      }
    })
    // Creation
    .addCase(actions.createWorkspaceReservation.pending, (state, action) => {
      registerMutation(state, {
        type: 'create',
        data: action.meta.arg,
        requestId: action.meta.requestId,
      });
    })
    .addCase(actions.createWorkspaceReservation.fulfilled, (state, action) => {
      addWorkspaceReservation(state, action.payload);
      resolveMutation(state, 'create', action.meta.requestId);
    })
    .addCase(actions.createWorkspaceReservation.rejected, (state, action) => {
      resolveMutation(state, 'create', action.meta.requestId);
    })

    // Patches
    .addCase(actions.saveWorkspaceReservation.pending, (state, action) => {
      registerMutation(state, {
        type: 'patch',
        data: {original: action.meta.arg.workspaceReservation, patch: action.meta.arg.patch},
        requestId: action.meta.requestId,
      });
    })
    .addCase(actions.saveWorkspaceReservation.fulfilled, (state, action) => {
      removeWorkspaceReservation(state, action.payload.id);
      addWorkspaceReservation(state, action.payload);

      // Invalidate invitations just in case they got modified
      delete state.childReservations[action.payload.id];

      resolveMutation(state, 'patch', action.meta.requestId);
    })
    .addCase(actions.saveWorkspaceReservation.rejected, (state, action) => {
      resolveMutation(state, 'patch', action.meta.requestId);
    })

    .addCase(actions.cancelWorkspaceReservation.pending, (state, action) => {
      registerMutation(state, {
        type: 'patch',
        data: {original: action.meta.arg.workspaceReservation},
        requestId: action.meta.requestId,
      });
    })
    .addCase(actions.cancelWorkspaceReservation.fulfilled, (state, action) => {
      removeWorkspaceReservation(state, action.payload.id);
      resolveMutation(state, 'patch', action.meta.requestId);
    })
    .addCase(actions.cancelWorkspaceReservation.rejected, (state, action) => {
      resolveMutation(state, 'patch', action.meta.requestId);
    })
    .addCase(actions.acceptReservationInvitation.pending, (state, action) => {
      registerMutation(state, {
        type: 'patch',
        data: {original: action.meta.arg.workspaceReservation},
        requestId: action.meta.requestId,
      });
    })
    .addCase(actions.acceptReservationInvitation.fulfilled, (state, action) => {
      removeWorkspaceReservation(state, action.payload.id);
      addWorkspaceReservation(state, action.payload);
      resolveMutation(state, 'patch', action.meta.requestId);
    })
    .addCase(actions.acceptReservationInvitation.rejected, (state, action) => {
      resolveMutation(state, 'patch', action.meta.requestId);
    })

    .addCase(actions.declineReservationInvitation.pending, (state, action) => {
      registerMutation(state, {
        type: 'patch',
        data: {original: action.meta.arg.workspaceReservation},
        requestId: action.meta.requestId,
      });
    })
    .addCase(actions.declineReservationInvitation.fulfilled, (state, action) => {
      removeWorkspaceReservation(state, action.payload.id);
      addWorkspaceReservation(state, action.payload);
      resolveMutation(state, 'patch', action.meta.requestId);
    })
    .addCase(actions.declineReservationInvitation.rejected, (state, action) => {
      resolveMutation(state, 'patch', action.meta.requestId);
    });
};

const removeWorkspaceReservation = (state: TimeslotsState, workspaceReservationId: string) => {
  for (const [date, entry] of Object.entries(state.workspaceReservationsByDate)) {
    if (entry?.status === 'fulfilled') {
      // This check makes the removal slightly more expensive but has the benefit of not recreating every array
      if (entry.data.some((r) => r.id === workspaceReservationId)) {
        entry.data = entry.data.filter((r) => r.id !== workspaceReservationId);
      }
    }
  }
};

const addWorkspaceReservation = (state: TimeslotsState, workspaceReservation: WorkspaceReservation) => {
  const dateKey = dateKeyForDateTimeString(workspaceReservation.localStart);
  const entry = state.workspaceReservationsByDate[dateKey];
  if (!entry) {
    throw new Error('Workspace reservation cannot be added to a day without loading existing data first');
  }

  if (entry.status !== 'fulfilled') {
    console.warn('Workspace reservation was added to day that is loading / failed to load');
  }

  const existing = entry.data ?? [];
  state.workspaceReservationsByDate[dateKey] = {
    status: 'fulfilled',
    data: existing.concat(workspaceReservation).sort(reservationCompare),
  };
};
